/**
 * section-skills.tsx — Mục "Kỹ năng & công cụ" của màn nhân viên AI (phần kỹ năng; công cụ ở section-tools.tsx):
 *   - Áp dụng Quy tắc an toàn chung (`settings.useSharedRules`) — Cần phát hành; nội dung quy tắc sửa ở Thư mục dùng chung.
 *   - Chủ động theo dõi khách hàng — quy tắc theo dõi (hiệu lực ngay, không phiên bản hoá)
 *   - Thu thập thông tin khách hàng · Cập nhật nhãn khách hàng · Học thông tin quảng cáo (sắp có) — Cần phát hành
 *   - Kỹ năng (kịch bản AI, `ai_scenarios`): kỹ năng đang áp cho nhân viên này + chọn thêm từ Thư mục dùng chung (tick = `botIds`);
 *     kỹ năng tạo ở đây mặc định chỉ áp cho nhân viên này (`botIds: [botId]`). Hiệu lực ngay khi lưu.
 *   Link sang Thư mục dùng chung mở ở TAB MỚI; danh sách kỹ năng có nút "Tải lại".
 */
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BellRing, BookMarked, ListChecks, Megaphone, Pencil, Plus, ShieldCheck, Tags, Trash2, Wand2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch, Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useCdpProperties } from '@/hooks/use-cdp'
import {
  useAiBots, useAiScenarios, fetchScenario, createScenario, updateScenario, deleteScenario, type ScenarioMeta,
} from '@/hooks/use-ai'
import { invalidateLibrary, useSetScenarioBots } from '@/hooks/use-ai-khung'
import { AI_PATHS } from '@/lib/ai-khung'
import { OpenInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { ChipGroup, TagInput } from '../train/train-ui'
import { InsertVariableButton } from '../train/insert-variable-button'
import { SettingsList, SettingsRow } from '../train/settings-row'
import { SettingDialog } from '../train/setting-dialog'
import { FollowupRulesDialog } from '../train/followup-rules-dialog'
import { useFollowupRules } from '../train/followup-api'
import { SYNC_PROFILE_FIELDS, insertAtCursor, slugKey, uniqueKey, type BotSettings, type CollectAttribute, type Condition } from '../train/bot-settings'
import type { SectionProps } from '../train/draft-form'
import { StaffPanel } from './staff-ui'
import { skillScopeText, splitSkills, toggleSkillBot } from './staff-lib'

/** Bỏ điều kiện trỏ tới thuộc tính AI đã xoá / đổi mã (chuyển người + tự ngừng). */
function pruneAgentConditions(x: BotSettings, keys: Set<string>): BotSettings {
  const ok = (c: Condition) => !c.variable.startsWith('agent.') || keys.has(c.variable.slice(6))
  return {
    ...x,
    handoff: { ...x.handoff, attributeRules: x.handoff.attributeRules.filter(ok) },
    autoStop: { ...x.autoStop, conditionGroups: x.autoStop.conditionGroups.map((g) => ({ conditions: g.conditions.filter(ok) })).filter((g) => g.conditions.length) },
  }
}

const typeLabel = (type: CollectAttribute['type']): string => i18n.t(`aiStaff:skills.attrType.${type}`)

type SkillDialogKey = 'followup' | 'collect' | 'autoTag' | 'ads' | 'custom' | null

export function SkillsSection(props: SectionProps) {
  const { t } = useTranslation('aiStaff')
  const { botId, form, setSettings, canEdit } = props
  const s = form.settings
  const [dlg, setDlg] = useState<SkillDialogKey>(null)
  const [editing, setEditing] = useState<{ index: number | null; attr: CollectAttribute } | null>(null)
  const followupQ = useFollowupRules(botId)
  const { data: scData } = useAiScenarios()
  const scenarioCount = splitSkills(scData?.scenarios ?? [], botId).applied.length
  const close = (o: boolean) => { if (!o) setDlg(null) }

  const saveAttr = (attr: CollectAttribute, index: number | null) => {
    setSettings((x) => {
      const list = [...x.collectAttributes]
      if (index === null) list.push(attr)
      else list[index] = attr
      const keys = new Set(list.map((a) => a.key))
      return pruneAgentConditions({ ...x, collectAttributes: list }, keys)
    })
    setEditing(null)
  }
  const removeAttr = (index: number) => {
    setSettings((x) => {
      const list = x.collectAttributes.filter((_, i) => i !== index)
      const keys = new Set(list.map((a) => a.key))
      return pruneAgentConditions({ ...x, collectAttributes: list }, keys)
    })
  }

  return (
    <StaffPanel
      id="staff-ky-nang" title={t('skills.title')} icon={<Wand2 className="h-4 w-4" />} scope="publish"
      description={t('skills.description')}
    >
      <div className="flex items-start gap-3 rounded-lg border px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px]">{t('skills.sharedRules.title')}</p>
          <p className="text-xs text-muted-foreground">
            {s.useSharedRules ? t('skills.sharedRules.on') : t('skills.sharedRules.off')}
            {' · '}<OpenInNewTab to={AI_PATHS.library}>{t('skills.sharedRules.viewInLibrary')}</OpenInNewTab>
          </p>
        </div>
        <Switch checked={s.useSharedRules} onCheckedChange={(v) => setSettings((x) => ({ ...x, useSharedRules: v }))} disabled={!canEdit} aria-label={t('skills.sharedRules.title')} />
      </div>
      <SettingsList>
        <SettingsRow kind="dialog" icon={<BookMarked className="h-4 w-4" />} title={t('skills.custom.title')} count={scenarioCount} onOpen={() => setDlg('custom')} disabled={!canEdit}
          info={t('skills.custom.info')} />
        <SettingsRow kind="dialog" icon={<BellRing className="h-4 w-4" />} title={t('skills.followup.title')} count={followupQ.data?.rules.length ?? null} onOpen={() => setDlg('followup')} disabled={!canEdit}
          info={t('skills.followup.info')} />
        <SettingsRow kind="dialog" icon={<ListChecks className="h-4 w-4" />} title={t('skills.collect.title')} count={s.collectAttributes.length} onOpen={() => setDlg('collect')} disabled={!canEdit}
          info={t('skills.collect.info')} />
        <SettingsRow kind="status" icon={<Tags className="h-4 w-4" />} title={t('skills.autoTag.title')} on={s.autoTag.enabled} onOpen={() => setDlg('autoTag')} disabled={!canEdit}
          info={t('skills.autoTag.info')} />
        <SettingsRow kind="status" icon={<Megaphone className="h-4 w-4" />} title={t('skills.ads.title')} on={s.adLearning.enabled} onOpen={() => setDlg('ads')} tag="soon"
          info={t('skills.ads.info')} />
      </SettingsList>

      <FollowupRulesDialog open={dlg === 'followup'} onOpenChange={close} botId={botId} canEdit={canEdit} attributes={s.collectAttributes} />

      {/* ── Thu thập thông tin ── */}
      <Dialog open={dlg === 'collect'} onOpenChange={close}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> {t('skills.collect.title')}</DialogTitle>
            <DialogDescription>{t('skills.collect.dialogDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{t('skills.collect.count', { count: s.collectAttributes.length })}</p>
            {canEdit && (
              <Button size="sm" variant="outline" disabled={s.collectAttributes.length >= 30}
                onClick={() => setEditing({ index: null, attr: { key: '', name: '', description: '', type: 'text', scope: 'contact', syncField: null } })}>
                <Plus className="h-3.5 w-3.5" /> {t('skills.collect.add')}
              </Button>
            )}
          </div>
          {s.collectAttributes.length === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">{t('skills.collect.empty')}</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {s.collectAttributes.map((a, i) => (
                <li key={a.key} className="flex items-center gap-2 px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{a.name} <code className="text-[10px] text-muted-foreground">{a.key}</code></p>
                    {a.description && <p className="truncate text-xs text-muted-foreground">{a.description}</p>}
                  </div>
                  <Badge variant="outline" className="text-[10px]">{typeLabel(a.type)}</Badge>
                  <Badge variant="secondary" className="text-[10px]">{a.scope === 'contact' ? t('skills.collect.scopeContact') : t('skills.collect.scopeConversation')}</Badge>
                  {a.syncField && <Badge variant="default" className="text-[10px]">{t('skills.collect.sync')}</Badge>}
                  {canEdit && (
                    <>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing({ index: i, attr: a })} aria-label={t('common:actions.edit')}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeAttr(i)} aria-label={t('common:actions.delete')}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end"><Button variant="ghost" onClick={() => setDlg(null)}>{t('common:actions.close')}</Button></div>
        </DialogContent>
      </Dialog>

      {/* ── Gắn nhãn ── */}
      <SettingDialog
        open={dlg === 'autoTag'} onOpenChange={close} icon={<Tags className="h-4 w-4" />} title={t('skills.autoTag.title')} canEdit={canEdit}
        description={t('skills.autoTag.info')}
        value={s.autoTag} onSave={(v) => setSettings((x) => ({ ...x, autoTag: v }))}
        enabledKey={{ get: (c) => c.enabled, set: (c, on) => ({ ...c, enabled: on }) }} enabledLabel={t('skills.autoTag.enabledLabel')}
      >
        {(c, upd) => (
          <div className="space-y-1">
            <Label className="text-xs">{t('skills.autoTag.label')} <span className="font-normal text-muted-foreground">{t('skills.autoTag.labelHint')}</span></Label>
            <TagInput value={c.tags} onChange={(v) => upd((d) => ({ ...d, tags: v }))} placeholder={t('skills.autoTag.placeholder')} disabled={!canEdit} max={50} />
          </div>
        )}
      </SettingDialog>

      {/* ── Học thông tin quảng cáo (sắp có) ── */}
      <SettingDialog
        open={dlg === 'ads'} onOpenChange={close} icon={<Megaphone className="h-4 w-4" />} canEdit={false}
        title={<span className="flex items-center gap-2">{t('skills.ads.title')} <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{t('skills.ads.soon')}</span></span>}
        description={t('skills.ads.info')}
        value={s.adLearning} onSave={(v) => setSettings((x) => ({ ...x, adLearning: v }))}
        enabledKey={{ get: (a) => a.enabled, set: (a, on) => ({ ...a, enabled: on }) }} enabledLabel={t('skills.ads.enabledLabel')}
      >
        {() => (
          <p className="rounded-md bg-muted/30 p-3 text-sm text-muted-foreground">
            {t('skills.ads.notAvailable')}
          </p>
        )}
      </SettingDialog>

      {/* ── Kỹ năng tuỳ chỉnh ── */}
      <Dialog open={dlg === 'custom'} onOpenChange={close}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookMarked className="h-4 w-4 text-primary" /> {t('skills.custom.title')}</DialogTitle>
            <DialogDescription>{t('skills.custom.dialogDesc')}</DialogDescription>
          </DialogHeader>
          <CustomSkills botId={botId} canEdit={canEdit} attributes={s.collectAttributes} />
          <div className="flex justify-end"><Button variant="ghost" onClick={() => setDlg(null)}>{t('common:actions.close')}</Button></div>
        </DialogContent>
      </Dialog>

      {editing && (
        <AttributeDialog
          initial={editing.attr}
          takenKeys={s.collectAttributes.filter((_, i) => i !== editing.index).map((a) => a.key)}
          onClose={() => setEditing(null)}
          onSave={(a) => saveAttr(a, editing.index)}
        />
      )}
    </StaffPanel>
  )
}

function AttributeDialog({ initial, takenKeys, onClose, onSave }: {
  initial: CollectAttribute
  takenKeys: string[]
  onClose: () => void
  onSave: (a: CollectAttribute) => void
}) {
  const { t } = useTranslation('aiStaff')
  const [a, setA] = useState<CollectAttribute>(initial)
  const [optionsText, setOptionsText] = useState((initial.options ?? []).join(', '))
  const { data: propData } = useCdpProperties()
  const props = propData?.properties ?? []
  const isNew = !initial.key

  const submit = () => {
    const name = a.name.trim()
    if (!name) return void toast.error(t('skills.attr.nameRequired'))
    const options = optionsText.split(',').map((o) => o.trim()).filter(Boolean)
    if (a.type === 'select' && options.length === 0) return void toast.error(t('skills.attr.optionsRequired'))
    const key = isNew ? uniqueKey(slugKey(name), takenKeys) : a.key
    onSave({
      ...a,
      key,
      name,
      description: a.description.trim(),
      ...(a.type === 'select' ? { options } : { options: undefined }),
      syncField: a.scope === 'contact' ? a.syncField ?? null : null,
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? t('skills.attr.addTitle') : t('skills.attr.editTitle')}</DialogTitle>
          <DialogDescription>{t('skills.attr.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{t('skills.attr.name')}</Label>
            <Input value={a.name} maxLength={60} onChange={(e) => setA({ ...a, name: e.target.value })} placeholder={t('skills.attr.namePlaceholder')} />
          </div>
          <div>
            <Label className="text-xs">{t('skills.attr.desc')}</Label>
            <Textarea rows={2} maxLength={300} value={a.description} onChange={(e) => setA({ ...a, description: e.target.value })} placeholder={t('skills.attr.descPlaceholder')} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t('skills.attr.type')}</Label>
            <ChipGroup value={a.type} options={[{ value: 'text', label: typeLabel('text') }, { value: 'select', label: typeLabel('select') }, { value: 'number', label: typeLabel('number') }]} onChange={(v) => setA({ ...a, type: v })} />
          </div>
          {a.type === 'select' && (
            <div>
              <Label className="text-xs">{t('skills.attr.options')}</Label>
              <Input value={optionsText} onChange={(e) => setOptionsText(e.target.value)} placeholder={t('skills.attr.optionsPlaceholder')} />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">{t('skills.attr.scope')}</Label>
            <ChipGroup
              value={a.scope}
              options={[{ value: 'contact', label: t('skills.attr.scopeContact') }, { value: 'conversation', label: t('skills.attr.scopeConversation') }]}
              onChange={(v) => setA({ ...a, scope: v, syncField: v === 'conversation' ? null : a.syncField })}
            />
          </div>
          {a.scope === 'contact' && (
            <div>
              <Label className="text-xs">{t('skills.attr.syncField')} <span className="font-normal text-muted-foreground">{t('skills.attr.syncFieldHint')}</span></Label>
              <Select value={a.syncField ?? '__none__'} onValueChange={(v) => setA({ ...a, syncField: v === '__none__' ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('skills.attr.noSync')}</SelectItem>
                  {SYNC_PROFILE_FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  {props.map((p) => <SelectItem key={p.id} value={`prop:${p.id}`}>{t('skills.attr.propertyOption', { name: p.name })}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button onClick={submit}>{isNew ? t('common:actions.add') : t('common:actions.save')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Kỹ năng xử lý tình huống (kịch bản AI trong Thư mục dùng chung, áp theo botIds) ─────────────

type SkillForm = { id: string | null; name: string; description: string; content: string; triggerHints: string; loadMode: string; enabled: boolean; priority: number; botIds: string[] | null }

function CustomSkills({ botId, canEdit, attributes }: { botId: string; canEdit: boolean; attributes: CollectAttribute[] }) {
  const { t } = useTranslation('aiStaff')
  const scenariosQ = useAiScenarios()
  const { data } = scenariosQ
  const { data: botsData } = useAiBots()
  const setBots = useSetScenarioBots()
  const bots = botsData?.bots ?? []
  const { applied, available } = splitSkills(data?.scenarios ?? [], botId)
  const [editing, setEditing] = useState<SkillForm | null>(null)

  const open = async (sc: ScenarioMeta | null) => {
    if (!sc) return setEditing({ id: null, name: '', description: '', content: '', triggerHints: '', loadMode: 'auto', enabled: true, priority: 0, botIds: [botId] })
    try {
      const { scenario } = await fetchScenario(sc.id)
      setEditing({ id: scenario.id, name: scenario.name, description: scenario.description, content: scenario.content, triggerHints: scenario.triggerHints ?? '', loadMode: scenario.loadMode, enabled: scenario.enabled, priority: scenario.priority ?? 0, botIds: scenario.botIds ?? sc.botIds ?? null })
    } catch (err) { toast.error(apiError(err)) }
  }
  const tick = async (sc: ScenarioMeta, on: boolean) => {
    if (!on && sc.botIds == null && !window.confirm(t('skills.list.confirmUntick', { name: sc.name }))) return
    try {
      await setBots.mutateAsync({ id: sc.id, botIds: toggleSkillBot(sc.botIds, botId, on, bots.map((b) => b.id)) })
    } catch (err) { toast.error(apiError(err)) }
  }

  const row = (sc: ScenarioMeta, on: boolean) => (
    <li key={sc.id} className="flex items-center gap-2 px-3 py-2">
      <input type="checkbox" checked={on} onChange={(e) => void tick(sc, e.target.checked)} disabled={!canEdit || setBots.isPending} aria-label={on ? t('skills.list.untickAria', { name: sc.name }) : t('skills.list.tickAria', { name: sc.name })} />
      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => void open(sc)} disabled={!canEdit}>
        <p className="truncate text-sm">{sc.name}{!sc.enabled && <span className="ml-1 text-xs text-muted-foreground">{t('skills.list.disabled')}</span>}</p>
        <p className="truncate text-xs text-muted-foreground">{sc.description}</p>
      </button>
      <Badge variant="outline" className="shrink-0 text-[10px]" title={t('skills.list.appliesTo')}>{skillScopeText(sc.botIds, botId, bots)}</Badge>
      <Badge variant="secondary" className="shrink-0 text-[10px]">{sc.loadMode === 'always' ? t('skills.loadMode.always') : t('skills.loadMode.auto')}</Badge>
    </li>
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{t('skills.list.applied', { n: applied.length })}</p>
        <span className="flex flex-wrap items-center gap-1">
          <ReloadButton onReload={() => scenariosQ.refetch()} what={t('skills.list.reloadWhat')} iconOnly />
          {canEdit && <Button size="sm" variant="outline" onClick={() => void open(null)}><Plus className="h-3.5 w-3.5" /> {t('skills.list.create')}</Button>}
        </span>
      </div>
      {applied.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">{t('skills.list.noneApplied')}</p>
      ) : (
        <ul className="divide-y rounded-lg border">{applied.map((sc) => row(sc, true))}</ul>
      )}
      <div className="space-y-1.5">
        <p className="text-sm font-medium">{t('skills.list.available', { n: available.length })}</p>
        {available.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('skills.list.noMore')} <OpenInNewTab to={AI_PATHS.library}>{t('skills.list.openLibrary')}</OpenInNewTab></p>
        ) : (
          <ul className="max-h-64 divide-y overflow-y-auto rounded-lg border">{available.map((sc) => row(sc, false))}</ul>
        )}
      </div>
      {editing && <SkillDialog botId={botId} initial={editing} canEdit={canEdit} attributes={attributes} onClose={() => setEditing(null)} />}
    </div>
  )
}

const COND_MAX = 500
const INSTR_MAX = 2000

function SkillDialog({ botId, initial, canEdit, attributes, onClose }: { botId: string; initial: SkillForm; canEdit: boolean; attributes: CollectAttribute[]; onClose: () => void }) {
  const { t } = useTranslation('aiStaff')
  const qc = useQueryClient()
  const [f, setF] = useState<SkillForm>(initial)
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)

  const save = async () => {
    if (!f.name.trim()) return void toast.error(t('skills.dialog.nameRequired'))
    if (!f.description.trim()) return void toast.error(t('skills.dialog.conditionRequired'))
    if (!f.content.trim()) return void toast.error(t('skills.dialog.instructionRequired'))
    setBusy(true)
    try {
      const body = { name: f.name.trim(), description: f.description.trim(), content: f.content, triggerHints: f.triggerHints || null, loadMode: f.loadMode, enabled: f.enabled, priority: Number.isFinite(f.priority) ? f.priority : 0 }
      // Tạo ở màn một nhân viên AI → chỉ áp cho nhân viên này; sửa thì giữ phạm vi đang có.
      if (f.id) await updateScenario(f.id, body)
      else await createScenario({ ...body, botIds: f.botIds ?? [botId] })
      await invalidateLibrary(qc)
      toast.success(f.id ? t('skills.dialog.saved') : t('skills.dialog.created'))
      onClose()
    } catch (err) { toast.error(apiError(err)) } finally { setBusy(false) }
  }
  const remove = async () => {
    if (!f.id || !window.confirm(t('skills.dialog.confirmDelete', { name: f.name }))) return
    try {
      await deleteScenario(f.id)
      await invalidateLibrary(qc)
      onClose()
    } catch (err) { toast.error(apiError(err)) }
  }
  const insert = (token: string) => {
    const el = ref.current
    const { text, cursor } = insertAtCursor(f.content, token, el?.selectionStart, el?.selectionEnd)
    setF({ ...f, content: text })
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(cursor, cursor) })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{f.id ? t('skills.dialog.editTitle') : t('skills.list.create')}</DialogTitle>
          <DialogDescription>{f.id ? t('skills.dialog.editDesc') : t('skills.dialog.createDesc')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
            <div>
              <Label className="text-xs">{t('skills.dialog.name')}</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t('skills.dialog.namePlaceholder')} disabled={!canEdit} />
            </div>
            <div>
              <Label className="text-xs">{t('skills.dialog.whenUse')}</Label>
              <Select value={f.loadMode} onValueChange={(v) => setF({ ...f, loadMode: v })} disabled={!canEdit}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{t('skills.loadMode.auto')}</SelectItem>
                  <SelectItem value="always">{t('skills.loadMode.always')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('skills.dialog.condition')} <span className="font-normal text-muted-foreground">{t('skills.dialog.conditionHint')}</span></Label>
            <Textarea rows={2} maxLength={COND_MAX} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t('skills.dialog.conditionPlaceholder')} disabled={!canEdit} />
            <p className="text-right text-[11px] text-muted-foreground">{f.description.length}/{COND_MAX}</p>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label className="text-xs">{t('skills.dialog.instruction')}</Label>
              <InsertVariableButton botId={botId} onInsert={insert} attributes={attributes} disabled={!canEdit} />
            </div>
            <textarea
              ref={ref}
              className="min-h-[180px] w-full resize-y rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
              maxLength={INSTR_MAX}
              value={f.content}
              onChange={(e) => setF({ ...f, content: e.target.value })}
              placeholder={t('skills.dialog.instructionPlaceholder', { search: '{{tool.products.search}}', handoff: '{{tool.handoff}}' })}
              disabled={!canEdit}
            />
            <p className={`text-right text-[11px] ${f.content.length >= INSTR_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>{f.content.length}/{INSTR_MAX}</p>
          </div>
          <div>
            <Label className="text-xs">{t('skills.dialog.hints')} <span className="font-normal text-muted-foreground">{t('skills.dialog.hintsHint')}</span></Label>
            <Input value={f.triggerHints} onChange={(e) => setF({ ...f, triggerHints: e.target.value })} placeholder={t('skills.dialog.hintsPlaceholder')} disabled={!canEdit} />
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-28">
              <Label className="text-xs" title={t('skills.dialog.priorityTitle')}>{t('skills.dialog.priority')}</Label>
              <Input
                type="number"
                value={f.priority}
                onChange={(e) => setF({ ...f, priority: Number(e.target.value) || 0 })}
                title={t('skills.dialog.priorityTitle')}
                disabled={!canEdit}
              />
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm">
              <Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} disabled={!canEdit} /> {t('skills.dialog.enabled')}
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          {f.id && canEdit ? <Button variant="ghost" className="text-destructive" onClick={() => void remove()}><Trash2 className="h-4 w-4" /> {t('common:actions.delete')}</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>{canEdit ? t('common:actions.cancel') : t('common:actions.close')}</Button>
            {canEdit && <Button onClick={() => void save()} disabled={busy}>{busy ? t('skills.dialog.saving') : t('skills.dialog.save')}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
